/**
 * OutlineSlideCard - Sleek collapsible slide card
 *
 * Design: Modern, minimal, world-class
 * - Collapsed: Compact row for easy reordering
 * - Expanded: Two-column layout (content left, preview right)
 * - No redundant borders or boxes
 * - Elegant whitespace and typography
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Trash2, Sparkles, ChevronDown, ChevronUp, GripVertical, Lightbulb, Loader2, Check, AlertCircle, Settings2, FileText, Volume2 } from 'lucide-react';
import { MiniAudioPlayer } from './MiniAudioPlayer';
import type { SlideOutline, TemplateLayout, NarrativePosition, NarrativeFlow, StoryboardMeta } from '@/types/outline';
import { hasEnhancedContent } from '@/types/outline';
import { archetypePreviewUrl, TIER_BADGE_STYLE, TIER_LABEL } from './archetypePreview';
import { useKubicleAllowed } from '@/hooks/useKubicleAllowed';
import { StoryboardDetail } from './StoryboardDetail';
import { parseStoryboardPayload, storyboardMetaFromParsed } from '@/lib/parseStoryboardPayload';
import { ArchetypePicker } from './ArchetypePicker';
import type { KubicleArchetype } from '@/config/kubicleArchetypes';
import { useOutlineEditQueueStore } from '@/stores/outlineEditQueueStore';
import type { SlideGenerationStatus } from '@/stores/videoWorkspaceStore';

interface OutlineSlideCardProps {
  slide: SlideOutline;
  index: number;
  totalSlides: number;
  layouts: TemplateLayout[];
  onUpdate: (updates: Partial<SlideOutline>) => void;
  onRemove?: () => void;
  onOpenLayoutSelector: () => void;
  isReadOnly?: boolean;
  isDragging?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  defaultExpanded?: boolean;
  /** Generation status for this slide */
  generationStatus?: SlideGenerationStatus;
  /** Thinking text during generation */
  generatingThinking?: string;
  /** Whether this is an audio-first video (show audioRange/scriptSegment) */
  isAudioFirst?: boolean;
  /** Narration script text from the narration cache (may differ from scriptSegment) */
  narrationScript?: string | null;
  /** Whether audio has been generated for this slide */
  hasAudio?: boolean;
  /** Audio duration in seconds */
  audioDuration?: number | null;
  /** Slide database ID for audio player */
  slideDbId?: string;
  /** Current audio version — threaded into the player so a re-narrated slide busts the cache. */
  audioVersion?: number | null;
  /** Currently playing audio slide ID (for single-player coordination) */
  playingSlideId?: string | null;
  /** Callback when audio play state changes */
  onAudioPlayChange?: (slideId: string, playing: boolean) => void;
  /** Project/conversation id — used to queue storyboard edits to the chat composer. */
  conversationId?: string;
  /** Master audio URL for previewing the window in the seam (split-correction) editor. */
  seamAudioUrl?: string;
}

// Narrative position options for dropdown
const NARRATIVE_POSITIONS: { value: NarrativePosition; label: string }[] = [
  { value: 'opening', label: 'Opening' },
  { value: 'setup', label: 'Setup' },
  { value: 'build', label: 'Build' },
  { value: 'climax', label: 'Climax' },
  { value: 'resolution', label: 'Resolution' },
  { value: 'closing', label: 'Closing' },
];

// Format seconds into a readable time string
function formatAudioTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(1);
  return mins > 0 ? `${mins}:${secs.padStart(4, '0')}` : `${secs}s`;
}

export const OutlineSlideCard: React.FC<OutlineSlideCardProps> = ({
  slide,
  index,
  totalSlides: _totalSlides,
  layouts,
  onUpdate,
  onRemove,
  onOpenLayoutSelector,
  isReadOnly = false,
  isDragging = false,
  dragHandleProps,
  defaultExpanded = true,
  generationStatus,
  generatingThinking,
  isAudioFirst = false,
  narrationScript,
  hasAudio = false,
  audioDuration,
  slideDbId,
  audioVersion,
  playingSlideId,
  onAudioPlayChange,
  conversationId,
  seamAudioUrl,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const selectedLayout = layouts.find(l => l.layoutId === slide.layoutId);
  // Disciplined-compose: the Outline IS the storyboard — the scene's archetype + its golden preview.
  // Gated to the Kubicle allowlist (the full storyboard flow is beta for that group only).
  // Uses the EFFECTIVE viewer email so admin impersonation of an allowlisted user works.
  const kubicleAllowed = useKubicleAllowed();
  // Effective storyboard: prefer the structured field; else fall back to parsing visualInstructions
  // (lights up disciplined videos planned before the storyboard field existed).
  const parsedStoryboard = useMemo(() => parseStoryboardPayload(slide, slide.storyboard), [slide]);
  const storyboard = kubicleAllowed ? (slide.storyboard ?? storyboardMetaFromParsed(parsedStoryboard)) : undefined;
  // The unified "scene view": an archetype storyboard (Kubicle), an audio-first scene (general
  // video, where the design intent is the template layout) or a scene with a generated picture
  // (Kubicle 1A and 1B). StoryboardDetail renders all three: it falls back to the layout
  // preview/name when there's no archetype or picture.
  const showSceneView = !!storyboard || isAudioFirst || !!slide.storyboardImage?.url;
  // On a Kubicle 1A or 1B scene the outline's description is the template picker's note ("cast · title"), not words for a reviewer.
  const showDescription = !storyboard && !slide.storyboardImage?.url;
  // Optimistic archetype switch: show the chosen archetype immediately + a "Pending" badge until the
  // agent re-plans and the outline catches up.
  const pendingArchetypeKey = useOutlineEditQueueStore((s) => (conversationId ? s.pendingArchetypes[conversationId]?.[slide.id] : undefined));
  const isArchetypePending = !!pendingArchetypeKey && pendingArchetypeKey !== slide.storyboard?.archetypeKey;
  const displayArchetypeKey = pendingArchetypeKey || storyboard?.archetypeKey;
  const archetypeThumb = archetypePreviewUrl(displayArchetypeKey ? { archetypeKey: displayArchetypeKey } : storyboard);
  // Storyboard scenes use blank-canvas nominal layouts whose layoutThumbnail 404s — prefer the
  // real archetype preview. Non-storyboard slides keep the template layout thumbnail.
  const thumbnailUrl = slide.storyboardImage?.url ?? (storyboard
    ? (archetypeThumb || slide.layoutThumbnail)
    : (slide.layoutThumbnail || selectedLayout?.thumbnail || archetypeThumb));
  const [thumbFailed, setThumbFailed] = useState(false);
  useEffect(() => { setThumbFailed(false); }, [thumbnailUrl]);
  const clearPendingArchetype = useOutlineEditQueueStore((s) => s.clearPendingArchetype);
  useEffect(() => {
    if (conversationId && pendingArchetypeKey && slide.storyboard?.archetypeKey === pendingArchetypeKey) {
      clearPendingArchetype(conversationId, slide.id);
    }
  }, [conversationId, pendingArchetypeKey, slide.storyboard?.archetypeKey, slide.id, clearPendingArchetype]);
  const hasLayouts = layouts.length > 0;
  // Show the layout/thumbnail affordances when the template has layouts, or this scene carries an archetype or a generated picture.
  const hasVisual = hasLayouts || !!storyboard?.archetypeKey || !!slide.storyboardImage?.url;
  const [showArchetypePicker, setShowArchetypePicker] = useState(false);

  // Switch a disciplined-compose scene's archetype: QUEUE a re-plan instruction (pill above the chat
  // composer). It is sent with the user's next message; the agent then re-plans + updates the outline.
  const handleArchetypeSwitch = useCallback((next: KubicleArchetype) => {
    setShowArchetypePicker(false);
    if (!conversationId) return;
    const from = storyboard?.archetypeKey || slide.layoutName || 'the current archetype';
    const sceneLabel = `scene ${index + 1}${slide.title ? ` ("${slide.title}")` : ''} [${slide.id}]`;
    useOutlineEditQueueStore.getState().enqueue(conversationId, {
      kind: 'archetype',
      slideId: slide.id,
      summary: `Switch scene ${index + 1}: ${from} → ${next.key}`,
      message:
        `For ${sceneLabel}, change the archetype from ${from} to ${next.key}. ` +
        `Re-plan this scene: keep the narration verbatim, rewrite the act shot-list (visualInstructions) ` +
        `for ${next.key}'s structure, and update the outline.`,
      dedupeKey: `archetype:${slide.id}`,
    });
    // Optimistic: show the new archetype's preview immediately + a "Pending" badge until the agent re-plans.
    useOutlineEditQueueStore.getState().setPendingArchetype(conversationId, slide.id, next.key);
  }, [storyboard?.archetypeKey, slide.layoutName, slide.id, slide.title, index, conversationId]);

  // ============================================================
  // LOCAL STATE MANAGEMENT (prevents polling from overwriting edits)
  // ============================================================

  // Local state for all editable fields
  const [localTitle, setLocalTitle] = useState(slide.title);
  const [localDescription, setLocalDescription] = useState(slide.description || '');
  const [localKeyPoints, setLocalKeyPoints] = useState<string[]>(slide.keyPoints || []);
  const [localVisualInstructions, setLocalVisualInstructions] = useState(slide.visualInstructions || '');
  const [localAudienceTakeaway, setLocalAudienceTakeaway] = useState(slide.audienceTakeaway || '');
  const [localNarrativeFlow, setLocalNarrativeFlow] = useState<NarrativeFlow | undefined>(slide.narrativeFlow);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Refs to track editing state and prevent race conditions
  const isEditingRef = useRef(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSlideIdRef = useRef(slide.id);
  const onUpdateRef = useRef(onUpdate);
  // Refs so saveToParent can read latest storyboard/conversationId without churning its deps.
  const storyboardRef = useRef(storyboard);
  storyboardRef.current = storyboard;
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;

  // Keep onUpdate ref current (avoids dependency array issues)
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  // Sync local state with props ONLY when slide changes (not on every prop update)
  // This prevents polling updates from overwriting active edits
  useEffect(() => {
    if (slide.id !== lastSlideIdRef.current) {
      // Slide changed - sync all local state from props
      setLocalTitle(slide.title);
      setLocalDescription(slide.description || '');
      setLocalKeyPoints(slide.keyPoints || []);
      setLocalVisualInstructions(slide.visualInstructions || '');
      setLocalAudienceTakeaway(slide.audienceTakeaway || '');
      setLocalNarrativeFlow(slide.narrativeFlow);
      setHasUnsavedChanges(false);
      isEditingRef.current = false;
      lastSlideIdRef.current = slide.id;

      // Clear any pending save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    }
  }, [slide.id, slide.title, slide.description, slide.keyPoints, slide.visualInstructions, slide.audienceTakeaway, slide.narrativeFlow]);

  // Save function - call immediately to update store, parent handles API debounce
  const saveToParent = useCallback(() => {
    if (isReadOnly) return;

    const updates = {
      title: localTitle,
      description: localDescription || undefined,
      keyPoints: localKeyPoints,
      visualInstructions: localVisualInstructions || undefined,
      audienceTakeaway: localAudienceTakeaway || undefined,
      narrativeFlow: localNarrativeFlow,
    };
    console.log('💾 OutlineSlideCard: Saving updates to parent', { slideId: slide.id, updates });
    onUpdateRef.current(updates);
    setHasUnsavedChanges(false);
    isEditingRef.current = false;

    // Storyboard scenes: queue an instruction so the agent re-plans/regenerates the edited scene to
    // match the new text. Collapses to one pill per scene (dedupeKey). Other flows just save as before.
    if (storyboardRef.current && conversationIdRef.current) {
      useOutlineEditQueueStore.getState().enqueue(conversationIdRef.current, {
        kind: 'edit',
        slideId: slide.id,
        summary: `Edit scene ${index + 1}: ${localTitle || 'untitled'}`,
        message:
          `Scene ${index + 1} ("${localTitle}") [${slide.id}] was edited in the outline ` +
          `(title / description / key points). Re-plan or regenerate this scene to match the updated text, keeping the narration.`,
        dedupeKey: `edit:${slide.id}`,
      });
    }
  }, [localTitle, localDescription, localKeyPoints, localVisualInstructions, localAudienceTakeaway, localNarrativeFlow, isReadOnly, slide.id, index]);

  // Debounced auto-save effect - short debounce just to batch rapid keystrokes
  useEffect(() => {
    if (!hasUnsavedChanges || isReadOnly) return;

    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Short debounce (300ms) - just enough to batch rapid typing
    saveTimeoutRef.current = setTimeout(() => {
      saveToParent();
    }, 300);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [localTitle, localDescription, localKeyPoints, localVisualInstructions, localAudienceTakeaway, localNarrativeFlow, hasUnsavedChanges, isReadOnly, saveToParent]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Helper to mark field as editing and changed
  const markEditing = useCallback(() => {
    isEditingRef.current = true;
    setHasUnsavedChanges(true);
  }, []);

  // ============================================================
  // END LOCAL STATE MANAGEMENT
  // ============================================================

  // Check if this slide has any enhanced content fields
  const slideHasAdvancedContent = hasEnhancedContent(slide);

  const isGenerating = generationStatus === 'generating';
  const isCompleted = generationStatus === 'completed';
  const isFailed = generationStatus === 'failed';

  // Status badge component
  const StatusBadge = () => {
    if (isGenerating) {
      return (
        <div className="flex items-center gap-1.5 px-1.5 md:px-2 py-0.5 rounded-full bg-accent dark:bg-primary/20 text-primary dark:text-accent-foreground flex-shrink-0">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span className="text-[10px] font-medium hidden md:inline">Generating</span>
        </div>
      );
    }
    if (isCompleted) {
      return (
        <div className="flex items-center gap-1 px-1.5 md:px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex-shrink-0">
          <Check className="w-3 h-3" />
          <span className="text-[10px] font-medium hidden md:inline">Generated</span>
        </div>
      );
    }
    if (isFailed) {
      return (
        <div className="flex items-center gap-1 px-1.5 md:px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 flex-shrink-0">
          <AlertCircle className="w-3 h-3" />
          <span className="text-[10px] font-medium hidden md:inline">Failed</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className={cn(
        "group relative rounded-xl overflow-hidden",
        "bg-white dark:bg-zinc-900",
        "border border-gray-200 dark:border-zinc-800",
        "hover:border-gray-300 dark:hover:border-zinc-700",
        "transition-all duration-200",
        isDragging && "shadow-2xl scale-[1.02] ring-2 ring-primary/50"
      )}
    >
      {/* Collapsed View */}
      {!isExpanded && (
        <div
          className="flex items-center gap-2 md:gap-3 px-3 md:px-4 py-3 cursor-pointer"
          onClick={() => setIsExpanded(true)}
        >
          {/* Drag Handle - Always show for reordering (independent of read-only content editing) */}
          {dragHandleProps && (
            <div
              className="cursor-grab active:cursor-grabbing touch-none p-1 -ml-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-800"
              onClick={(e) => e.stopPropagation()}
              {...dragHandleProps}
            >
              <GripVertical className="w-4 h-4 text-gray-400 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-300" />
            </div>
          )}

          {/* Slide Number */}
          <span className="text-xs font-bold text-primary dark:text-accent-foreground w-5">
            {String(index + 1).padStart(2, '0')}
          </span>

          {/* Title & Description */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {localTitle || 'Untitled'}
              </span>
              {hasUnsavedChanges && (
                <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" title="Unsaved changes" />
              )}
            </div>
            {storyboard ? (
              <>
                <StoryboardBadges storyboard={storyboard} pending={isArchetypePending} />
                {storyboard.selectionRationale && (
                  <p className="text-xs text-gray-400 dark:text-zinc-500 truncate mt-0.5">
                    {storyboard.selectionRationale}
                  </p>
                )}
              </>
            ) : showDescription && localDescription && (
              <p className="text-xs text-gray-400 dark:text-zinc-500 truncate">
                {localDescription}
              </p>
            )}
          </div>

          {/* Audio Time + Status Badge (audio-first) */}
          {isAudioFirst && slide.audioRange && (
            <span className="text-[11px] text-gray-400 dark:text-zinc-500 tabular-nums flex-shrink-0">
              {formatAudioTime(slide.audioRange.startTime)} – {formatAudioTime(slide.audioRange.endTime)}
            </span>
          )}
          {isAudioFirst && hasAudio && (
            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 flex-shrink-0">
              <Volume2 className="w-3 h-3 text-emerald-500" />
            </div>
          )}

          {/* Status — hidden for storyboard scenes (generation is chat-driven) */}
          {!storyboard && <StatusBadge />}

          {/* Thumbnail — layout preview, or the archetype's golden preview for disciplined-compose */}
          {hasVisual && (
            <div className="w-14 h-8 rounded overflow-hidden bg-gray-100 dark:bg-zinc-800 flex-shrink-0">
              {thumbnailUrl && !thumbFailed ? (
                <img src={thumbnailUrl} alt="" className="w-full h-full object-cover" onError={() => setThumbFailed(true)} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[7px] text-gray-400 dark:text-zinc-500 px-1 text-center leading-tight truncate">
                  {storyboard?.archetypeKey || slide.layoutName}
                </div>
              )}
            </div>
          )}

          {/* Expand Icon */}
          <ChevronDown className="w-4 h-4 text-gray-400 dark:text-zinc-500" />
        </div>
      )}

      {/* Expanded View */}
      {isExpanded && (
        <div className="flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-2 md:gap-3 px-3 md:px-4 py-3 border-b border-gray-100 dark:border-zinc-800/50">
            {/* Drag Handle - Always show for reordering (independent of read-only content editing) */}
            {dragHandleProps && (
              <div
                className="cursor-grab active:cursor-grabbing touch-none p-1 -ml-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 flex-shrink-0"
                onClick={(e) => e.stopPropagation()}
                {...dragHandleProps}
              >
                <GripVertical className="w-4 h-4 text-gray-400 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-300" />
              </div>
            )}

            <span className="text-xs font-bold text-primary dark:text-accent-foreground flex-shrink-0">
              {String(index + 1).padStart(2, '0')}
            </span>

            <input
              type="text"
              value={localTitle}
              onChange={(e) => {
                markEditing();
                setLocalTitle(e.target.value);
              }}
              onBlur={() => {
                if (hasUnsavedChanges) saveToParent();
              }}
              disabled={isReadOnly}
              className={cn(
                "flex-1 min-w-0 text-sm font-semibold bg-transparent outline-none",
                "text-gray-900 dark:text-white",
                "placeholder:text-gray-300 dark:placeholder:text-zinc-600"
              )}
              placeholder="Slide title..."
            />

            {/* Unsaved changes indicator */}
            {hasUnsavedChanges && (
              <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" title="Unsaved changes" />
            )}

            {/* Show Advanced Toggle — hidden for storyboard scenes (the StoryboardDetail is the source of truth) */}
            {!storyboard && (
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={cn(
                "flex items-center gap-1 px-1.5 md:px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors flex-shrink-0",
                showAdvanced || slideHasAdvancedContent
                  ? "bg-accent dark:bg-primary/20 text-primary dark:text-accent-foreground"
                  : "bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-zinc-400 hover:text-primary"
              )}
              title="Show visual instructions and narrative context"
            >
              <Settings2 className="w-3 h-3" />
              <span className="hidden md:inline">Advanced</span>
              {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            )}

            {/* Status — hidden for storyboard scenes (generation is chat-driven, not per-card) */}
            {!storyboard && <StatusBadge />}

            {!isReadOnly && onRemove && (
              <button
                onClick={onRemove}
                className="p-1.5 rounded-lg text-gray-300 dark:text-zinc-600 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors flex-shrink-0"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}

            <button
              onClick={() => setIsExpanded(false)}
              className="p-1.5 rounded-lg text-gray-400 dark:text-zinc-500 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors flex-shrink-0"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
          </div>

          {/* Two-Column Content — stacks on mobile */}
          <div className="flex flex-col md:flex-row">
            {/* Left: Content Fields */}
            <div className="flex-1 p-3 space-y-3">
              {/* Description — hidden for storyboard scenes (StoryboardDetail is the source of truth) */}
              {showDescription && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-zinc-500 font-medium mb-1.5 block">
                  Description
                </label>
                <textarea
                  value={localDescription}
                  onChange={(e) => {
                    markEditing();
                    setLocalDescription(e.target.value);
                  }}
                  onBlur={() => {
                    if (hasUnsavedChanges) saveToParent();
                  }}
                  disabled={isReadOnly}
                  rows={2}
                  className={cn(
                    "w-full text-sm leading-relaxed resize-none",
                    "bg-transparent outline-none",
                    "text-gray-600 dark:text-zinc-300",
                    "placeholder:text-gray-300 dark:placeholder:text-zinc-600",
                    "focus:text-gray-900 dark:focus:text-white",
                    "transition-colors"
                  )}
                  placeholder="Describe what this slide should contain..."
                />
              </div>
              )}

              {/* Scene view — archetype storyboard (Kubicle) OR audio-first scene (layout as intent) */}
              {showSceneView && (
                <div className="rounded-xl border border-gray-100 dark:border-zinc-800/60 bg-gray-50/40 dark:bg-white/[0.02] p-3">
                  <StoryboardDetail
                    slide={slide}
                    storyboard={storyboard}
                    conversationId={conversationId}
                    sceneNumber={index + 1}
                    onSwitchArchetype={storyboard && !isReadOnly ? () => setShowArchetypePicker(true) : undefined}
                    pendingArchetypeKey={isArchetypePending ? pendingArchetypeKey : undefined}
                    slideDbId={slideDbId}
                    audioVersion={audioVersion}
                    audioDuration={audioDuration}
                    hasAudio={hasAudio}
                    playingSlideId={playingSlideId}
                    onAudioPlayChange={onAudioPlayChange}
                    seamAudioUrl={seamAudioUrl}
                    isReadOnly={isReadOnly}
                  />
                </div>
              )}

              {/* Narration Section - Script + Audio Player (scene view shows narration in StoryboardDetail) */}
              {!showSceneView && (narrationScript || slide.scriptSegment || hasAudio || isAudioFirst) && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-zinc-500 font-medium mb-2 flex items-center gap-1.5">
                    <FileText className="w-3 h-3" />
                    Narration
                    {isAudioFirst && slide.audioRange && (
                      <span className="ml-auto text-[10px] font-normal tabular-nums text-gray-400 dark:text-zinc-500">
                        {formatAudioTime(slide.audioRange.startTime)} – {formatAudioTime(slide.audioRange.endTime)}
                        <span className="text-gray-300 dark:text-zinc-600 mx-1">·</span>
                        {formatAudioTime(slide.audioRange.endTime - slide.audioRange.startTime)}
                      </span>
                    )}
                  </label>
                  <div className="rounded-lg border border-gray-100 dark:border-zinc-700/50 overflow-hidden">
                    {(narrationScript || slide.scriptSegment) && (
                      <div className="px-3 py-2.5 bg-gray-50/50 dark:bg-zinc-800/20">
                        <p className="text-[13px] leading-relaxed text-gray-600 dark:text-zinc-300 whitespace-pre-wrap">
                          {narrationScript || slide.scriptSegment}
                        </p>
                      </div>
                    )}
                    {!narrationScript && !slide.scriptSegment && (
                      <div className="px-3 py-2.5 bg-gray-50/50 dark:bg-zinc-800/20">
                        <p className="text-[13px] text-gray-400 dark:text-zinc-500 italic">
                          Script not yet generated
                        </p>
                      </div>
                    )}
                    {hasAudio && slideDbId && (
                      <div className="px-3 py-2 border-t border-gray-100 dark:border-zinc-700/50 bg-gray-50 dark:bg-zinc-800/30">
                        <MiniAudioPlayer
                          slideId={slideDbId}
                          version={audioVersion}
                          audioDuration={audioDuration}
                          isPlaying={playingSlideId === slideDbId}
                          onPlayChange={(playing) => onAudioPlayChange?.(slideDbId, playing)}
                        />
                      </div>
                    )}
                    {!hasAudio && (narrationScript || slide.scriptSegment) && (
                      <div className="px-3 py-1.5 border-t border-gray-100 dark:border-zinc-700/50 bg-gray-50 dark:bg-zinc-800/30">
                        <div className="flex items-center gap-1.5 text-[10px] text-gray-400 dark:text-zinc-500">
                          <Volume2 className="w-3 h-3" />
                          Audio pending
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Advanced Content — Key Points + Visual Instructions + Narrative Flow (non-storyboard only) */}
              {!storyboard && showAdvanced && (
                <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-zinc-800/50">
                  {/* Key Points */}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-zinc-500 font-medium mb-1.5 block">
                      Key Points
                    </label>
                    <div className="space-y-1">
                      {localKeyPoints.map((point, pointIndex) => (
                        <div key={pointIndex} className="flex items-start gap-2 group/point">
                          <span className="text-primary mt-0.5 text-sm">•</span>
                          <input
                            type="text"
                            value={point}
                            onChange={(e) => {
                              markEditing();
                              const newPoints = [...localKeyPoints];
                              newPoints[pointIndex] = e.target.value;
                              setLocalKeyPoints(newPoints);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !isReadOnly) {
                                e.preventDefault();
                                markEditing();
                                const newPoints = [...localKeyPoints];
                                newPoints.splice(pointIndex + 1, 0, '');
                                setLocalKeyPoints(newPoints);
                                setTimeout(() => {
                                  const inputs = e.currentTarget.parentElement?.parentElement?.querySelectorAll('input');
                                  inputs?.[pointIndex + 1]?.focus();
                                }, 0);
                              }
                              if (e.key === 'Backspace' && !point && localKeyPoints.length > 1) {
                                e.preventDefault();
                                markEditing();
                                const newPoints = [...localKeyPoints];
                                newPoints.splice(pointIndex, 1);
                                setLocalKeyPoints(newPoints);
                                setTimeout(() => {
                                  const inputs = e.currentTarget.parentElement?.parentElement?.querySelectorAll('input');
                                  inputs?.[Math.max(0, pointIndex - 1)]?.focus();
                                }, 0);
                              }
                            }}
                            onBlur={() => {
                              if (!point.trim() && localKeyPoints.length > 1) {
                                markEditing();
                                const newPoints = localKeyPoints.filter((_, i) => i !== pointIndex);
                                setLocalKeyPoints(newPoints);
                              }
                              if (hasUnsavedChanges) {
                                setTimeout(() => saveToParent(), 0);
                              }
                            }}
                            disabled={isReadOnly}
                            className={cn(
                              "flex-1 text-sm bg-transparent outline-none leading-relaxed",
                              "text-gray-600 dark:text-zinc-300",
                              "placeholder:text-gray-300 dark:placeholder:text-zinc-600",
                              "focus:text-gray-900 dark:focus:text-white",
                              "transition-colors"
                            )}
                            placeholder="Add key point..."
                          />
                          {!isReadOnly && localKeyPoints.length > 1 && (
                            <button
                              onClick={() => {
                                markEditing();
                                const newPoints = localKeyPoints.filter((_, i) => i !== pointIndex);
                                setLocalKeyPoints(newPoints);
                              }}
                              className="opacity-0 group-hover/point:opacity-100 p-0.5 text-gray-300 dark:text-zinc-600 hover:text-red-500 transition-all"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                      {!isReadOnly && (
                        <button
                          onClick={() => {
                            markEditing();
                            setLocalKeyPoints([...localKeyPoints, '']);
                          }}
                          className="flex items-center gap-2 text-xs text-gray-400 dark:text-zinc-500 hover:text-primary dark:hover:text-accent-foreground transition-colors mt-1"
                        >
                          <span className="text-sm">+</span>
                          <span>Add point</span>
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Visual Instructions — the act shot-list sent verbatim to the slide builder */}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-zinc-500 font-medium mb-2 block">
                      {storyboard ? 'Shot list · sent to the slide builder' : 'Visual Instructions'}
                    </label>
                    <textarea
                      value={localVisualInstructions}
                      onChange={(e) => {
                        markEditing();
                        setLocalVisualInstructions(e.target.value);
                      }}
                      onBlur={() => {
                        if (hasUnsavedChanges) saveToParent();
                      }}
                      disabled={isReadOnly}
                      rows={4}
                      className={cn(
                        "w-full text-sm leading-relaxed resize-none rounded-lg p-3",
                        "bg-gray-50 dark:bg-zinc-800/50",
                        "border border-gray-200 dark:border-zinc-700",
                        "outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30",
                        "text-gray-600 dark:text-zinc-300",
                        "placeholder:text-gray-400 dark:placeholder:text-zinc-500"
                      )}
                      placeholder="Detailed instructions for the content planner. Include specific values (e.g., '$100,000 investment'), what elements to show, and visual emphasis..."
                    />
                  </div>

                  {/* Audience Takeaway */}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-zinc-500 font-medium mb-2 block">
                      Audience Takeaway
                    </label>
                    <textarea
                      value={localAudienceTakeaway}
                      onChange={(e) => {
                        markEditing();
                        setLocalAudienceTakeaway(e.target.value);
                      }}
                      onBlur={() => {
                        if (hasUnsavedChanges) saveToParent();
                      }}
                      disabled={isReadOnly}
                      rows={2}
                      className={cn(
                        "w-full text-sm leading-relaxed resize-none rounded-lg p-3",
                        "bg-gray-50 dark:bg-zinc-800/50",
                        "border border-gray-200 dark:border-zinc-700",
                        "outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30",
                        "text-gray-600 dark:text-zinc-300",
                        "placeholder:text-gray-400 dark:placeholder:text-zinc-500"
                      )}
                      placeholder="What the viewer should understand or feel after seeing this slide..."
                    />
                  </div>

                  {/* Narrative Flow */}
                  <div className="space-y-3">
                    <label className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-zinc-500 font-medium block">
                      Narrative Flow
                    </label>

                    {/* Position Selector */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-zinc-400 w-16">Position:</span>
                      <select
                        value={localNarrativeFlow?.position || ''}
                        onChange={(e) => {
                          markEditing();
                          const position = e.target.value as NarrativePosition | '';
                          setLocalNarrativeFlow({
                            ...localNarrativeFlow,
                            position: position || undefined,
                          });
                        }}
                        disabled={isReadOnly}
                        className={cn(
                          "text-xs rounded-lg px-2 py-1.5",
                          "bg-gray-50 dark:bg-zinc-800/50",
                          "border border-gray-200 dark:border-zinc-700",
                          "outline-none focus:ring-2 focus:ring-primary/20",
                          "text-gray-600 dark:text-zinc-300"
                        )}
                      >
                        <option value="">Not set</option>
                        {NARRATIVE_POSITIONS.map(pos => (
                          <option key={pos.value} value={pos.value}>{pos.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Comes From */}
                    <div>
                      <span className="text-xs text-gray-500 dark:text-zinc-400 mb-1 block">Builds on (from previous slides):</span>
                      <textarea
                        value={localNarrativeFlow?.comesFrom || ''}
                        onChange={(e) => {
                          markEditing();
                          setLocalNarrativeFlow({
                            ...localNarrativeFlow,
                            comesFrom: e.target.value || undefined,
                          });
                        }}
                        onBlur={() => {
                          if (hasUnsavedChanges) saveToParent();
                        }}
                        disabled={isReadOnly}
                        rows={2}
                        className={cn(
                          "w-full text-sm leading-relaxed resize-none rounded-lg p-2",
                          "bg-gray-50 dark:bg-zinc-800/50",
                          "border border-gray-200 dark:border-zinc-700",
                          "outline-none focus:ring-2 focus:ring-primary/20",
                          "text-gray-600 dark:text-zinc-300",
                          "placeholder:text-gray-400 dark:placeholder:text-zinc-500"
                        )}
                        placeholder="What context from previous slides this builds on..."
                      />
                    </div>

                    {/* Leads To */}
                    <div>
                      <span className="text-xs text-gray-500 dark:text-zinc-400 mb-1 block">Sets up (for following slides):</span>
                      <textarea
                        value={localNarrativeFlow?.leadsTo || ''}
                        onChange={(e) => {
                          markEditing();
                          setLocalNarrativeFlow({
                            ...localNarrativeFlow,
                            leadsTo: e.target.value || undefined,
                          });
                        }}
                        onBlur={() => {
                          if (hasUnsavedChanges) saveToParent();
                        }}
                        disabled={isReadOnly}
                        rows={2}
                        className={cn(
                          "w-full text-sm leading-relaxed resize-none rounded-lg p-2",
                          "bg-gray-50 dark:bg-zinc-800/50",
                          "border border-gray-200 dark:border-zinc-700",
                          "outline-none focus:ring-2 focus:ring-primary/20",
                          "text-gray-600 dark:text-zinc-300",
                          "placeholder:text-gray-400 dark:placeholder:text-zinc-500"
                        )}
                        placeholder="What this slide prepares the viewer for..."
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Layout preview — only when NOT in scene view (scene view shows the frame in StoryboardDetail) */}
            {hasVisual && !showSceneView && (() => {
              // Non-storyboard slides only: template layouts switch via the layout picker.
              const canSwitch = !isReadOnly && hasLayouts;
              const onClickPreview = () => { if (canSwitch) onOpenLayoutSelector(); };
              const previewLabel = slide.layoutName;
              return (
              <div className={cn(
                "p-3 border-t md:border-t-0 md:border-l border-gray-100 dark:border-zinc-800/50",
                "w-full md:w-auto",
                isAudioFirst ? "md:w-40" : "md:w-48"
              )}>
                <button
                  onClick={() => canSwitch && onClickPreview()}
                  disabled={!canSwitch}
                  className={cn(
                    "w-full group/layout",
                    !canSwitch && "cursor-default"
                  )}
                >
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 dark:bg-zinc-800 mb-2">
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt={previewLabel}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-gray-400 dark:text-zinc-500">
                        {previewLabel}
                      </div>
                    )}

                    {canSwitch && (
                      <div className="absolute inset-0 bg-black/0 group-hover/layout:bg-black/50 flex items-center justify-center transition-all">
                        <span className="text-white text-xs font-medium opacity-0 group-hover/layout:opacity-100 transition-opacity">
                          Change
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className={cn(
                      "text-xs font-medium text-gray-500 dark:text-zinc-400 transition-colors",
                      storyboard ? "font-mono truncate" : "group-hover/layout:text-primary",
                    )}>
                      {previewLabel}
                    </span>
                    {canSwitch && (
                      <span className="text-[10px] text-gray-400 dark:text-zinc-500 opacity-0 group-hover/layout:opacity-100 transition-opacity">
                        Edit →
                      </span>
                    )}
                  </div>
                </button>

                {slide.aiReasoning && (
                  <div className="mt-3 p-2 rounded-lg bg-accent dark:bg-primary/10 border border-primary/30">
                    <div className="flex items-start gap-1.5">
                      <Lightbulb className="w-3 h-3 text-primary dark:text-accent-foreground mt-0.5 flex-shrink-0" />
                      <p className="text-[10px] leading-relaxed text-primary dark:text-accent-foreground">
                        {slide.aiReasoning}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Generation Overlay - Shows when slide is being generated */}
      {isGenerating && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm rounded-xl z-10">
          <div className="text-center px-4">
            {/* Animated sparkles icon */}
            <div className="relative w-10 h-10 mx-auto mb-3">
              <Sparkles className="w-10 h-10 text-primary animate-pulse" />
              <div
                className="absolute inset-0 rounded-full border-2 border-primary/30 animate-ping"
                style={{ animationDuration: '2s' }}
              />
            </div>

            {/* Thinking text */}
            {generatingThinking && (
              <div className="text-sm text-accent-foreground line-clamp-2 max-w-[200px] mb-2">
                {generatingThinking}
              </div>
            )}

            {/* Progress dots */}
            <div className="flex justify-center gap-1">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce"
                  style={{ animationDelay: `${i * 150}ms` }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Archetype switcher (disciplined-compose) — asks the agent to re-plan the scene */}
      {showArchetypePicker && storyboard && (
        <ArchetypePicker
          currentArchetypeKey={storyboard.archetypeKey}
          narrationShape={storyboard.narrationShape}
          onConfirm={handleArchetypeSwitch}
          onClose={() => setShowArchetypePicker(false)}
        />
      )}
    </div>
  );
};

/** Compact intent + tier badges shown under the scene title (disciplined-compose storyboard). */
const StoryboardBadges: React.FC<{ storyboard: StoryboardMeta; pending?: boolean }> = ({ storyboard, pending }) => {
  const tier = storyboard.tier ?? 'A';
  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
      {pending && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-400/20 dark:text-amber-300 text-[10px] font-semibold">
          Pending
        </span>
      )}
      {storyboard.narrationShape && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-accent text-accent-foreground dark:bg-primary/20 text-[10px] font-medium">
          {storyboard.narrationShape}
        </span>
      )}
      <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium', TIER_BADGE_STYLE[tier] ?? TIER_BADGE_STYLE.A)}>
        {TIER_LABEL[tier] ?? `Tier ${tier}`}
      </span>
      {storyboard.surface && (
        <span className="text-[10px] text-gray-400 dark:text-zinc-500">{storyboard.surface}</span>
      )}
    </div>
  );
};
